'use client';
import type { FieldDefinitionInput } from '@phinest/shared';
import { cn } from '../cn';
import { Badge } from './badge';
import { ChipInput } from './chips';
import { Input, Textarea } from './inputs';
import { Select } from './select';
import { Switch } from './switch';
import { MoneyText, DateText } from './text';

export type FieldDef = Pick<FieldDefinitionInput, 'key' | 'label' | 'type' | 'options' | 'required' | 'description' | 'validation'>;

/** Read-only rendering of a custom field value. */
export function FieldValue({ def, value, currency = 'CAD', timeZone, className }: { def: FieldDef; value: unknown; currency?: string; timeZone?: string; className?: string }) {
  if (value === null || value === undefined || value === '') return <span className={cn('text-text-3', className)}>—</span>;
  switch (def.type) {
    case 'currency':
      return <MoneyText amount={value as number} currency={currency} className={className} />;
    case 'percent':
      return <span className={cn('tabular', className)}>{Number(value)}%</span>;
    case 'number':
      return <span className={cn('tabular', className)}>{new Intl.NumberFormat().format(Number(value))}</span>;
    case 'boolean':
      return <Badge tone={value ? 'green' : 'gray'} size="sm" className={className}>{value ? 'Yes' : 'No'}</Badge>;
    case 'date':
      return <DateText value={String(value)} format="date" className={className} />;
    case 'datetime':
      return <DateText value={String(value)} format="datetime" timeZone={timeZone} className={className} />;
    case 'select': {
      const opt = def.options?.find((o) => o.value === value);
      return <Badge tone="blue" size="sm" className={className}>{opt?.label ?? String(value)}</Badge>;
    }
    case 'multi_select': {
      const arr = Array.isArray(value) ? value : [value];
      return (
        <span className={cn('inline-flex flex-wrap gap-1', className)}>
          {arr.map((v) => (
            <Badge key={String(v)} tone="gray" size="sm">{def.options?.find((o) => o.value === v)?.label ?? String(v)}</Badge>
          ))}
        </span>
      );
    }
    case 'email':
      return <a href={`mailto:${value}`} className={cn('text-accent hover:underline', className)}>{String(value)}</a>;
    case 'phone':
      return <a href={`tel:${value}`} className={cn('text-accent hover:underline', className)}>{String(value)}</a>;
    case 'url':
      return <a href={String(value)} target="_blank" rel="noreferrer" className={cn('text-accent hover:underline break-all', className)}>{String(value).replace(/^https?:\/\//, '')}</a>;
    case 'json':
      return <code className={cn('text-[12px] font-[var(--font-mono)] text-text-2', className)}>{JSON.stringify(value)}</code>;
    case 'long_text':
      return <span className={cn('whitespace-pre-wrap', className)}>{String(value)}</span>;
    default:
      return <span className={className}>{String(value)}</span>;
  }
}

/** Editable control for a custom field. Emits values already in storage shape. */
export function FieldEditor({ def, value, onChange, id, disabled }: { def: FieldDef; value: unknown; onChange: (value: unknown) => void; id?: string; disabled?: boolean }) {
  const str = value === null || value === undefined ? '' : String(value);
  switch (def.type) {
    case 'boolean':
      return <Switch id={id} checked={!!value} onCheckedChange={(v) => onChange(v)} disabled={disabled} />;
    case 'number':
    case 'currency':
    case 'percent':
      return <Input id={id} type="number" step={def.type === 'number' ? 'any' : '0.01'} min={def.validation?.min} max={def.validation?.max} value={str} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} disabled={disabled} leading={def.type === 'currency' ? <span className="text-[13px]">$</span> : undefined} trailing={def.type === 'percent' ? <span className="text-[13px]">%</span> : undefined} />;
    case 'date':
      return <Input id={id} type="date" value={str} onChange={(e) => onChange(e.target.value || null)} disabled={disabled} />;
    case 'datetime':
      return <Input id={id} type="datetime-local" value={str ? str.slice(0, 16) : ''} onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : null)} disabled={disabled} />;
    case 'select':
      return <Select value={str || null} onValueChange={(v) => onChange(v)} options={(def.options ?? []).map((o) => ({ value: o.value, label: o.label ?? o.value }))} disabled={disabled} id={id} />;
    case 'multi_select':
      return <ChipInput id={id} value={Array.isArray(value) ? (value as string[]) : []} onChange={(v) => onChange(v)} suggestions={(def.options ?? []).map((o) => o.value)} placeholder="Add…" />;
    case 'long_text':
      return <Textarea id={id} value={str} onChange={(e) => onChange(e.target.value || null)} disabled={disabled} />;
    case 'json':
      return <Textarea id={id} value={typeof value === 'string' ? value : value ? JSON.stringify(value, null, 2) : ''} onChange={(e) => { try { onChange(e.target.value ? JSON.parse(e.target.value) : null); } catch { onChange(e.target.value); } }} disabled={disabled} className="font-[var(--font-mono)] text-[12.5px]" />;
    case 'email':
      return <Input id={id} type="email" value={str} onChange={(e) => onChange(e.target.value || null)} disabled={disabled} />;
    case 'phone':
      return <Input id={id} type="tel" value={str} onChange={(e) => onChange(e.target.value || null)} disabled={disabled} />;
    case 'url':
      return <Input id={id} type="url" value={str} onChange={(e) => onChange(e.target.value || null)} disabled={disabled} placeholder="https://" />;
    default:
      return <Input id={id} value={str} onChange={(e) => onChange(e.target.value || null)} disabled={disabled} maxLength={def.validation?.max_length} />;
  }
}
